import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, CheckCircle2, Calendar, MapPin, Phone, ShieldCheck, X, MessageCircle, ExternalLink } from "lucide-react";

/**
 * Student-facing notification bell.
 * Reads /notifications (booking_confirmed, owner_confirmed cards) and shows
 * a badge with unread count. Each item is tappable and expands with details.
 */
export default function StudentNotifications() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const wrapRef = useRef();

  const load = () => api.get("/notifications").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 45000); return () => clearInterval(t); }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target) && !expanded) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, expanded]);

  const unread = items.filter((i) => !i.read).length;

  const openItem = async (it) => {
    setExpanded(it);
    if (!it.read) {
      try {
        await api.post(`/notifications/${it.id}/read`);
        setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, read: true } : x));
      } catch { /* silent */ }
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        data-testid="student-notif-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative w-11 h-11 rounded-full bg-white border border-marigold-200 hover:border-vermilion-600 hover:bg-marigold-50 grid place-items-center transition-colors"
        aria-label="Notifications"
      >
        <Bell size={18} className="text-vermilion-600" />
        {unread > 0 && (
          <span data-testid="student-notif-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-bandhani-600 text-white text-[10px] font-black grid place-items-center border-2 border-white animate-pulse">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="student-notif-panel" className="absolute right-0 mt-2 w-[92vw] max-w-md bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-br from-marigold-50 to-turmeric-100">
            <div>
              <div className="text-[10px] tracking-widest uppercase font-bold text-vermilion-600">Your inbox</div>
              <div className="font-display font-black text-lg">Notifications</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded-full hover:bg-white"><X size={16} /></button>
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {items.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">Nothing here yet. Book a hostel to receive confirmations, owner replies, and check-in updates.</div>
            )}
            {items.map((it) => (
              <button
                key={it.id}
                type="button"
                data-testid={`student-notif-${it.id}`}
                onClick={() => openItem(it)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-marigold-50 transition-colors ${!it.read ? "bg-turmeric-50" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full grid place-items-center flex-shrink-0 ${it.type === 'owner_confirmed' ? 'bg-peacock-500 text-white' : 'bg-marigold-100 text-vermilion-700'}`}>
                    <CheckCircle2 size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-sm truncate">
                        {it.type === 'owner_confirmed' ? 'Owner confirmed your booking' : 'Booking confirmed'}
                      </div>
                      {!it.read && <span className="w-2 h-2 rounded-full bg-vermilion-600" />}
                    </div>
                    <div className="text-xs text-slate-600 truncate">{it.hostel_name}</div>
                    {it.check_in && (
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Calendar size={11} /> {it.check_in} → {it.check_out}</div>
                    )}
                    {it.verification_code && (
                      <div className="text-[10px] font-mono mt-1 inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-1.5 py-0.5">
                        <ShieldCheck size={9} /> {it.verification_code}
                      </div>
                    )}
                    <div className="text-[10px] text-vermilion-600 font-semibold mt-1">Tap for details →</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Detail modal */}
      {expanded && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => setExpanded(null)}>
          <div
            data-testid="student-notif-detail"
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`px-6 py-5 text-white flex items-start justify-between ${expanded.type === 'owner_confirmed' ? 'bg-peacock-gradient' : 'bg-gradient-to-br from-marigold-500 to-vermilion-600'}`}>
              <div>
                <div className="text-[10px] tracking-widest uppercase font-bold text-turmeric-300">
                  {expanded.type === 'owner_confirmed' ? "You're expected" : "Booking confirmed"}
                </div>
                <div className="font-display font-black text-xl mt-1">{expanded.hostel_name}</div>
                {expanded.amount && <div className="text-xs text-white/85 mt-1">Amount paid ₹{Math.round(expanded.amount).toLocaleString("en-IN")}</div>}
              </div>
              <button onClick={() => setExpanded(null)} className="p-1 rounded-full hover:bg-white/15"><X size={18} /></button>
            </div>

            <div className="p-6 space-y-4">
              {expanded.message && (
                <p className="text-sm text-slate-700 leading-relaxed">{expanded.message}</p>
              )}
              {expanded.verification_code && (
                <div className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-4">
                  <div className="text-[10px] tracking-widest uppercase font-bold text-emerald-700">Show this at check-in</div>
                  <div className="mt-1 font-mono font-black text-2xl tracking-widest text-emerald-800 select-all">{expanded.verification_code}</div>
                </div>
              )}
              {expanded.check_in && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                    <div className="text-[9px] tracking-widest uppercase font-bold text-slate-500">Check-in</div>
                    <div className="font-semibold text-sm mt-0.5">{expanded.check_in}</div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                    <div className="text-[9px] tracking-widest uppercase font-bold text-slate-500">Check-out</div>
                    <div className="font-semibold text-sm mt-0.5">{expanded.check_out}</div>
                  </div>
                </div>
              )}
              {expanded.owner_phone && (
                <div className="bg-marigold-50 border border-marigold-200 rounded-xl p-4">
                  <div className="text-[10px] tracking-widest uppercase font-bold text-vermilion-600">Owner contact</div>
                  <div className="mt-1 font-semibold">{expanded.owner_name}</div>
                  <a href={`tel:${expanded.owner_phone}`} className="mt-1 inline-flex items-center gap-1.5 text-sm font-mono text-vermilion-700 hover:underline">
                    <Phone size={13} /> {expanded.owner_phone}
                  </a>
                </div>
              )}
              {expanded.location && (
                <a href={expanded.maps_link || "#"} target="_blank" rel="noopener noreferrer" className="block bg-slate-50 border border-slate-200 rounded-xl p-4 hover:border-vermilion-500 transition-colors">
                  <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500 flex items-center gap-1"><MapPin size={10} /> Location</div>
                  <div className="mt-1 text-sm font-semibold">{expanded.location}</div>
                  <div className="text-xs text-vermilion-600 mt-1 inline-flex items-center gap-1">Open in Maps <ExternalLink size={10} /></div>
                </a>
              )}
              {expanded.whatsapp_owner_link && (
                <a href={expanded.whatsapp_owner_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold">
                  <MessageCircle size={14} /> WhatsApp owner
                </a>
              )}
              {expanded.booking_id && (
                <Link
                  to={`/bookings/${expanded.booking_id}`}
                  className="mt-2 block text-center text-xs font-semibold text-vermilion-600 hover:underline"
                  onClick={() => setExpanded(null)}
                >
                  Open full booking →
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
